import { useEffect, useState } from "react";
import { Mail, Phone, PhoneCall, Save, X } from "lucide-react";
import { toast } from "sonner";
import { eur, eur2 } from "@/lib/pricing";
import { apiError, authApi } from "@/lib/auth";
import { STATUSES, fmtDate, statusMeta } from "@/lib/orderStatus";

export default function OrderDetail({ order, onUpdated, onClose }) {
  const [note, setNote] = useState(order.internal_note || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => setNote(order.internal_note || ""), [order.id, order.internal_note]);

  const patch = async (body, okMsg) => {
    setBusy(true);
    try {
      const res = await authApi.patch(`/admin/orders/${order.id}`, body);
      onUpdated(res.data);
      if (okMsg) toast.success(okMsg);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  const c = order.customer;
  const tel = (c.phone || "").replace(/\s+/g, "");

  return (
    <div className="flex h-full flex-col bg-white" data-testid="order-detail">
      <div className="flex items-start justify-between border-b border-black/10 p-6">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-terracotta" data-testid="order-detail-code">{order.code}</p>
          <h3 className="mt-2 font-display text-2xl font-extrabold tracking-tighter" data-testid="order-detail-company">{c.company}</h3>
          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.16em] text-ink/45">Ricevuto {fmtDate(order.created_at)}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] ${statusMeta(order.status).chip}`} data-testid="order-detail-status">
            {statusMeta(order.status).label}
          </span>
          <button onClick={onClose} className="flex h-9 w-9 items-center justify-center border border-black/10 hover:bg-ink hover:text-white lg:hidden" aria-label="Chiudi" data-testid="order-detail-close">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      <div className="flex-1 space-y-8 overflow-y-auto p-6">
        <section>
          <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.24em] text-ink/45">Referente</p>
          <p className="font-display text-base font-bold" data-testid="order-detail-contact">{c.contact_name}</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <a href={`mailto:${c.email}`} className="flex h-10 items-center gap-2 border border-black/15 px-4 font-mono text-[10px] uppercase tracking-[0.14em] transition-colors hover:border-ink hover:bg-ink hover:text-white" data-testid="order-detail-email-link">
              <Mail className="h-3.5 w-3.5" /> {c.email}
            </a>
            {tel && (
              <a href={`tel:${tel}`} className="flex h-10 items-center gap-2 border border-black/15 px-4 font-mono text-[10px] uppercase tracking-[0.14em] transition-colors hover:border-ink hover:bg-ink hover:text-white" data-testid="order-detail-phone-link">
                <Phone className="h-3.5 w-3.5" /> {c.phone}
              </a>
            )}
          </div>
          {c.notes && (
            <p className="mt-4 border-l-2 border-cobalt pl-4 text-sm leading-relaxed text-ink/70" data-testid="order-detail-customer-notes">{c.notes}</p>
          )}
        </section>

        <section>
          <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.24em] text-ink/45">Articoli</p>
          <ul className="divide-y divide-black/10 border-y border-black/10" data-testid="order-detail-items">
            {order.items.map((it, i) => (
              <li key={i} className="flex items-center justify-between gap-4 py-3">
                <div className="min-w-0">
                  <p className="truncate font-display text-sm font-bold">{it.name} — {it.color}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink/45">
                    {it.sku} · {it.size_run} · {it.boxes} box × {it.box_size} = {it.pairs} paia · {eur2(it.pair_price)}/paio
                  </p>
                </div>
                <p className="font-mono text-sm font-semibold">{eur(it.line_total)}</p>
              </li>
            ))}
          </ul>
          <div className="mt-3 flex items-baseline justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.16em] text-ink/45">{order.total_boxes} box · {order.total_pairs} paia</p>
            <p className="font-display text-2xl font-extrabold tracking-tighter" data-testid="order-detail-total">{eur(order.subtotal)}</p>
          </div>
        </section>

        <section>
          <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.24em] text-ink/45">Stato</p>
          <div className="flex flex-wrap gap-2" data-testid="order-detail-status-buttons">
            {STATUSES.map((s) => (
              <button
                key={s.key}
                disabled={busy}
                onClick={() => patch({ status: s.key }, `Stato: ${s.label}`)}
                className={`border px-4 py-2 font-mono text-[10px] uppercase tracking-[0.14em] transition-colors duration-200 disabled:opacity-50 ${
                  order.status === s.key ? "border-ink bg-ink text-white" : "border-black/15 text-ink/60 hover:border-ink hover:text-ink"
                }`}
                data-testid={`order-status-${s.key}`}
              >
                {s.label}
              </button>
            ))}
          </div>
        </section>

        <section>
          <div className="mb-3 flex items-center justify-between">
            <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-ink/45">Ricontatto</p>
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink/60" data-testid="order-detail-last-contact">
              Ultimo: {fmtDate(order.last_contact_at)}
            </p>
          </div>
          <button
            disabled={busy}
            onClick={() => patch({ mark_contacted: true }, "Ricontatto registrato")}
            className="flex h-11 w-full items-center justify-center gap-2 bg-cobalt font-mono text-[10px] uppercase tracking-[0.2em] text-white transition-colors hover:bg-ink disabled:opacity-50"
            data-testid="order-mark-contacted"
          >
            <PhoneCall className="h-3.5 w-3.5" /> Segna ricontatto adesso
          </button>
        </section>

        <section>
          <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.24em] text-ink/45">Nota interna</p>
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={4}
            placeholder="Es. richiamare lunedì, vuole sconto su 5 box…"
            className="w-full border border-black/15 bg-paper px-4 py-3 text-sm text-ink placeholder:text-ink/35 focus:border-ink focus:outline-none"
            data-testid="order-detail-note"
          />
          <button
            disabled={busy || note === (order.internal_note || "")}
            onClick={() => patch({ internal_note: note }, "Nota salvata")}
            className="mt-2 flex h-10 items-center gap-2 border border-ink px-4 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors hover:bg-ink hover:text-white disabled:opacity-40"
            data-testid="order-detail-save-note"
          >
            <Save className="h-3.5 w-3.5" /> Salva nota
          </button>
        </section>
      </div>
    </div>
  );
}
